import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import i18n from "../../i18n/config";
import { CARD_DIRECTIVE_ROUTES } from "../../constants/enums";
import { MY_CARD_MISC_LINKS } from "../../constants/myCardLinks";

// types
import { RootState } from "../../types/redux";
import { NavigationPanelListItemType } from "../../types/components";
import { MyCardMenuItemType } from "../../types/api/card";

// hooks
import useMyCardData from "../../hooks/useMyCardData";
import useWindowDimensions from "../../services/useWindowDimensions";
import { useMyCardTransferLinks } from "../../hooks/useMyCardTransferLinks";

// ui components
import ApiErrorModal from "../../components/Common/Modals/ApiErrorModal";
import NavigationPanelList from "../../components/Navigation/NavigationPanelList";
import CardSection from "../../components/MyCard/Index/CardSection";
import SpinningLoader from "../../components/Common/SpinningLoader";
import MyCardDirectiveModal from "../../components/MyCard/MyCardDirectiveModal";

const MOBILE_BREAKPOINT = 768;

const routeFor = (directiveType: string): string | undefined =>
  directiveType in CARD_DIRECTIVE_ROUTES
    ? CARD_DIRECTIVE_ROUTES[directiveType as keyof typeof CARD_DIRECTIVE_ROUTES]
    : undefined;

const toNavigationPanelLinks = (links: MyCardMenuItemType[]) =>
  links.map((link) => ({
    title: link.text,
    directiveType: link.directiveType,
    id: link.id,
    modal: link.modal,
    link: link.modal ? undefined : routeFor(link.directiveType),
  }));

const MyCard = () => {
  const featureFlags = useSelector((state: RootState) => state.user.featureFlags);
  const [miscLinks, setMiscLinks] = useState<NavigationPanelListItemType[]>([]);
  const [showDirectiveModal, setShowDirectiveModal] = useState(false);
  const { width } = useWindowDimensions();
  const { response, loading, refetch } = useMyCardData();
  const { links: transferLinks, loading: recipientRequestLoading, restrictions } = useMyCardTransferLinks();

  useEffect(() => {
    if (response?.menuItems) {
      const responseMenuItems = toNavigationPanelLinks(response.menuItems);
      setMiscLinks([...responseMenuItems, ...MY_CARD_MISC_LINKS]);
    }
  }, [response?.menuItems]);

  useEffect(() => {
    if (restrictions) {
      setShowDirectiveModal(true);
    }
  }, [restrictions]);

  if (loading || !response || recipientRequestLoading) {
    return <SpinningLoader show />;
  }

  const isDesktop = width >= MOBILE_BREAKPOINT;

  return (
    <div
      className="v2 columns columns__2 columns__mycard spacing-bottom-medium gap-20"
      data-testid="my-card-index-page"
    >
      <section>
        <CardSection data={response} getMyCardData={refetch} />

        {/*
          Layout note: the misc panels live in a different part of the DOM tree
          per viewport. On desktop the layout is two dynamic-height columns, so
          CSS grid won't work and we can't keep these in the same flex section as
          the card without breaking the mobile order. Hence the JS width check.
        */}
        {isDesktop && (
          <article className="panel panel-default">
            <NavigationPanelList links={miscLinks} featureFlags={featureFlags} />
          </article>
        )}
      </section>

      {!isDesktop && (
        <section className="panel panel-default">
          <NavigationPanelList links={miscLinks} featureFlags={featureFlags} />
        </section>
      )}

      <section>
        <div className="padding-default-small rounded-lg border-bottom border-all border-gray-300">
          <NavigationPanelList
            links={transferLinks}
            featureFlags={featureFlags}
            disableHoverBgColor={true}
          />
        </div>
      </section>

      <p className="spacing-top-medium order-4 marengo-grey font-12-px line-height-150">
        {i18n.t("PAGES.MY_CARD.INDEX.TRANSFER_DISCLAIMER")}
      </p>

      <ApiErrorModal />

      <MyCardDirectiveModal
        open={showDirectiveModal}
        setOpen={setShowDirectiveModal}
        modalData={restrictions}
      />
    </div>
  );
};

export default MyCard;
